import {create} from 'mutative'
import type {Updater} from 'use-mutative'

import type {FullProduct, Product} from '@/models/productModel'
import {hashProduct} from '@/models/productModel'
import type {ProductId} from '@/routes/_shop/product/$productId'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useInteractedActionProps from '@/utils/hooks/useInteractedActionProps'
import style from '@/views/Product/Product.module.scss'
import {
  getLooseSelectedVariantFromSelectedOptions,
  getSelectedVariantFromSelectedOptions,
} from '@/views/Product/utils'

interface OptionButtonProps {
  name: string
  value: string
  select: (name: string, value: string, isAvailable: boolean) => void
  isSelected: boolean
  isAvailable: boolean
  otherOptions: (readonly [string, string])[]
  preload: (product: Pick<Product, 'retailer' | 'id'>) => void
  resolvedProductId: ProductId
}

const OptionButton = deepMemo<OptionButtonProps>(function OptionButton({
  otherOptions,
  isSelected,
  name,
  value,
  select,
  isAvailable,
  preload,
  resolvedProductId,
}) {
  const preloadAction = useCallback(() => {
    preload({
      retailer: resolvedProductId.retailer,
      id: resolvedProductId.id,
    })
  }, [preload, resolvedProductId.id, resolvedProductId.retailer])

  const interactedActionProps = useInteractedActionProps(preloadAction)

  return (
    <li>
      <button
        className={clsx(style.VariationValue, isSelected && style.Selected)}
        onClick={() => {
          select(name, value, isAvailable)
        }}
        data-is-not-available={!isAvailable}
        {...interactedActionProps}
        role='radio'
        aria-checked={isSelected}
      >
        {value}
        {!isAvailable && (
          <VisuallyHidden>
            {`(not available${
              otherOptions.length > 0
                ? ` for ${otherOptions.map(([name, value]) => `${name} ${value}`).join(', ')}`
                : ''
            })`}
          </VisuallyHidden>
        )}
      </button>
    </li>
  )
})(['resolvedProductId', 'otherOptions'])

interface ProductVariationsProps {
  options: Map<string, Map<string, boolean>>
  selectedOptions: Map<string, string>
  setSelectedOptions: Updater<Map<string, string>>
  variations: FullProduct['variations']
  retailer: FullProduct['retailer']
  preload: (product: Pick<Product, 'retailer' | 'id'>) => void
  unavailableProductIds: Set<string>
}

export default memo<ProductVariationsProps>(function ProductVariations({
  options,
  selectedOptions,
  variations,
  setSelectedOptions,
  preload,
  retailer,
  unavailableProductIds,
}) {
  const select = useCallback(
    (name: string, value: string, isAvailable: boolean) => {
      setSelectedOptions(selected => {
        if (selected.get(name) === value) {
          selected.delete(name)
        } else {
          if (!isAvailable) {
            // TODO: check, because the current variation field API currently only contains non out of stock variation
            // so if user select a variation that is out of stock (not exist in the variation array), it cannot get the product id, so currently we have to clear all other options hopefully make it available
            // but there is a case that the that variation option is out of stock entirely, clear selected will not help
            selected.clear()
          }
          selected.set(name, value)
        }
        return selected
      })
    },
    [setSelectedOptions],
  )

  const isSelected = useCallback(
    (name: string, value: string) => {
      return selectedOptions.get(name) === value
    },
    [selectedOptions],
  )

  return (
    <>
      {Array.from(options.entries()).map(([name, values]) => {
        const otherOptions = Array.from(options.keys())
          .filter(key => key !== name)
          .map(key => {
            const selected = selectedOptions.get(key)
            if (selected) {
              return [key, selected] as const
            }
            return false
          })
          .filter(Boolean)

        const optionsArray = Array.from(values.entries())
          .map(([value, isAvailable]) => {
            const draftSelectedOptions = create(selectedOptions, draft => {
              draft.set(name, value)
            })

            const exactResolvedProductId = getSelectedVariantFromSelectedOptions(
              variations,
              draftSelectedOptions,
            )

            const looseResolvedProductId = getLooseSelectedVariantFromSelectedOptions(
              variations,
              draftSelectedOptions,
              name,
            )

            const derivedResolvedProductId = exactResolvedProductId ?? looseResolvedProductId

            // Only show option button if the we can resolve product id from variation info, and its not broken before
            if (derivedResolvedProductId) {
              const hash = hashProduct({
                retailer,
                id: derivedResolvedProductId.id,
              })

              if (!unavailableProductIds.has(hash)) {
                return {
                  value,
                  isAvailable,
                  productId: derivedResolvedProductId,
                }
              }
            }

            return false
          })
          .filter(Boolean)

        return (
          // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- we have fieldset
          <tr key={name} className={style.Variation} role='none'>
            <td className={style.VariationName} aria-hidden>
              {name}
            </td>
            {/* eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role -- we have fieldset */}
            <td className={style.VariationValues} role='none'>
              <fieldset>
                <VisuallyHidden as='legend'>{name}</VisuallyHidden>
                <ul>
                  {optionsArray.length > 0 ? (
                    optionsArray.map(({value, isAvailable, productId}) => {
                      return (
                        <OptionButton
                          key={`${name}-${value}`}
                          isSelected={isSelected(name, value)}
                          select={select}
                          isAvailable={isAvailable}
                          name={name}
                          value={value}
                          otherOptions={otherOptions}
                          // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop -- already deepMemo
                          resolvedProductId={{
                            retailer,
                            id: productId.id,
                          }}
                          preload={preload}
                        />
                      )
                    })
                  ) : (
                    <div className={style.NoOptionAvailable}>No option available</div>
                  )}
                </ul>
              </fieldset>
            </td>
          </tr>
        )
      })}
    </>
  )
})
